// frontend/src/pages/ProcurementAnalysisPage.tsx
// v5.0 - WAR ROOM (Fixed Headers & Nested Providers)

import { useState, FC } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Box, Container, Paper, Typography, IconButton, Button, Tooltip,
    Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    CircularProgress, Chip, Stack, Collapse, Dialog, DialogTitle, DialogContent,
    DialogActions, TextField 
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InventoryIcon from '@mui/icons-material/Inventory';
import FactoryIcon from '@mui/icons-material/Factory';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RemoveIcon from '@mui/icons-material/Remove';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EditIcon from '@mui/icons-material/Edit';

import ProcurementChart from '../components/Procurement/ProcurementChart';
import axiosInstance from '../config/axiosInstance';

// --- ESTILOS ---
const glassCardSx = {
    bgcolor: '#1e293b', 
    border: '1px solid #334155',
    borderRadius: 2,
    p: 3,
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)'
};

// --- COMPONENTE DE TENDENCIA ---
const TrendIndicator: FC<{ value: number }> = ({ value }) => {
    if (!value || value === 0) return <RemoveIcon sx={{ color: '#64748b', fontSize: 16 }} />;
    const isUp = value > 0;
    return (
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
            {isUp ? <TrendingUpIcon sx={{ color: '#ef4444', fontSize: 18 }} /> : <TrendingDownIcon sx={{ color: '#4ade80', fontSize: 18 }} />}
            <Typography variant="caption" sx={{ color: isUp ? '#ef4444' : '#4ade80', fontWeight: 700 }}>
                {Math.abs(value * 100).toFixed(1)}%
            </Typography>
        </Stack>
    );
};

// --- SUB-COMPONENTE: FILA PROVEEDOR CON DESGLOSE ---
const ProviderRow: FC<{ row: any; familyId: string }> = ({ row, familyId }) => {
    const [open, setOpen] = useState(false);

    // Fetch SKUs específicos de este proveedor al abrir
    const { data: providerSkus = [], isLoading } = useQuery({
        queryKey: ['providerSkus', familyId, row.name],
        queryFn: async () => {
            const safeName = encodeURIComponent(row.name);
            return (await axiosInstance.get<any[]>(`/procurement/families/${familyId}/providers/${safeName}/skus`)).data;
        },
        enabled: open,
        staleTime: 1000 * 60 * 5
    });

    return (
        <>
            <TableRow hover sx={{ '&:last-child td': { borderBottom: 0 }, '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }}>
                <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <IconButton size="small" onClick={() => setOpen(!open)} sx={{ color: '#94a3b8' }}>
                            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                        <Typography sx={{ color: '#e2e8f0', fontWeight: 600 }}>{row.name}</Typography>
                    </Stack>
                </TableCell>
                <TableCell align="center" sx={{ color: '#94a3b8' }}>{row.record_count}</TableCell>
                <TableCell align="center"><TrendIndicator value={row.trend_pct} /></TableCell>
                <TableCell align="right" sx={{ color: '#38bdf8', fontWeight: 700 }}>
                    {row.total_spend.toLocaleString('es-ES', { style: 'currency', currency: row.currency, maximumFractionDigits: 0 })}
                </TableCell>
            </TableRow>
            
            {/* DESPLEGABLE */}
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0, borderBottom: 'none' }} colSpan={4}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ m: 2, ml: 6, p: 2, bgcolor: '#0f172a', borderRadius: 2, border: '1px solid #334155' }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, mb: 2, display: 'block' }}>
                                PRODUCTOS COMPRADOS A {row.name.toUpperCase()}
                            </Typography>
                            
                            {isLoading ? (
                                <CircularProgress size={20} />
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ color: '#64748b', borderBottom: '1px solid #1e293b' }}>Material</TableCell>
                                            <TableCell align="right" sx={{ color: '#64748b', borderBottom: '1px solid #1e293b' }}>Precio</TableCell>
                                            <TableCell align="center" sx={{ color: '#64748b', borderBottom: '1px solid #1e293b' }}>Tendencia</TableCell>
                                            <TableCell align="right" sx={{ color: '#64748b', borderBottom: '1px solid #1e293b' }}>Gasto</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {providerSkus.map((sku: any) => (
                                            <TableRow key={sku.id}>
                                                <TableCell sx={{ color: '#cbd5e1', fontFamily: 'monospace' }}>{sku.name}</TableCell>
                                                <TableCell align="right" sx={{ color: '#94a3b8', fontFamily: 'monospace' }}>
                                                    {sku.avg_price.toLocaleString('es-ES', { style: 'currency', currency: sku.currency || 'EUR', minimumFractionDigits: 2 })}
                                                </TableCell>
                                                <TableCell align="center"><TrendIndicator value={sku.trend_pct} /></TableCell>
                                                <TableCell align="right" sx={{ color: '#e2e8f0', fontWeight: 700, fontFamily: 'monospace' }}>
                                                    {sku.total_spend.toLocaleString('es-ES', { style: 'currency', currency: sku.currency || 'EUR', maximumFractionDigits: 0 })}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

const ProcurementAnalysisPage: FC = () => {
    const { familyId } = useParams<{ familyId: string }>();
    const navigate = useNavigate();
    const [tabIndex, setTabIndex] = useState(0);

    // 1. INFO FAMILIA
    const { data: familyInfo } = useQuery({
        queryKey: ['familyInfo', familyId],
        queryFn: async () => (await axiosInstance.get<any>(`/procurement/families`)).data.find((f: any) => f.id === familyId),
        enabled: !!familyId
    });

    // 2. DATA SKUS
    const { data: skus = [], isLoading: loadingSkus } = useQuery({
        queryKey: ['familySkus', familyId],
        queryFn: async () => (await axiosInstance.get<any[]>(`/procurement/families/${familyId}/skus`)).data,
        enabled: !!familyId
    });

    // 3. DATA PROVIDERS
    const { data: providers = [], isLoading: loadingProviders } = useQuery({
        queryKey: ['familyProviders', familyId],
        queryFn: async () => (await axiosInstance.get<any[]>(`/procurement/families/${familyId}/providers`)).data,
        enabled: !!familyId
    });

    if (!familyId) return null;

    const queryClient = useQueryClient(); // Necesario para refrescar
    const [editOpen, setEditOpen] = useState(false);
    const [newName, setNewName] = useState('');

    // Mutación para renombrar
    const renameMutation = useMutation({
        mutationFn: async () => {
            await axiosInstance.put(`/procurement/families/${familyId}`, { name: newName });
        },
        onSuccess: () => {
            setEditOpen(false);
            queryClient.invalidateQueries({ queryKey: ['familyInfo', familyId] }); // Recarga el título
        }
    });

    const handleOpenEdit = () => {
        if (familyInfo) setNewName(familyInfo.name);
        setEditOpen(true);
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 8 }}>
            
            {/* HEADER DE NAVEGACIÓN */}
            <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton onClick={() => navigate('/procurement/strategy')} sx={{ color: '#94a3b8', border: '1px solid #334155', bgcolor: '#1e293b' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Box>
                    <Typography variant="overline" sx={{ color: '#94a3b8', letterSpacing: 1, fontWeight: 700 }}>
                        ESTRATEGIA &gt; {familyInfo?.linked_ticker || 'SIN BENCHMARK'}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography variant="h4" sx={{ color: '#f8fafc', fontWeight: 700, textTransform: 'uppercase' }}>
                            {familyInfo ? familyInfo.name : 'Cargando...'}
                        </Typography>
                        
                        {/* BOTÓN DE EDICIÓN */}
                        <IconButton size="small" onClick={handleOpenEdit} sx={{ color: '#64748b', '&:hover': { color: '#38bdf8' } }}>
                            <EditIcon fontSize="small" />
                        </IconButton>

                        {familyInfo && (
                            <Chip 
                                label={familyInfo.total_spend.toLocaleString('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })} 
                                sx={{ bgcolor: 'rgba(56, 189, 248, 0.1)', color: '#38bdf8', border: '1px solid #38bdf8', fontWeight: 700 }}
                            />
                        )}
                    </Stack>
                </Box>
            </Box>

            {/* SECCIÓN 1: WAR ROOM (GRÁFICO) */}
            <Paper sx={{ ...glassCardSx, mb: 4 }}>
                <ProcurementChart familyId={familyId} />
            </Paper>

            {/* SECCIÓN 2: TABS */}
            <Box sx={{ borderBottom: 1, borderColor: '#334155', mb: 3 }}>
                <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} textColor="inherit" indicatorColor="primary" sx={{ '& .MuiTab-root': { color: '#94a3b8', '&.Mui-selected': { color: '#38bdf8' } } }}>
                    <Tab icon={<InventoryIcon />} iconPosition="start" label="Desglose por Material (SKU)" />
                    <Tab icon={<FactoryIcon />} iconPosition="start" label="Desglose por Proveedor" /> 
                </Tabs>
            </Box>

            {/* TABLA 1: SKUS */}
            {tabIndex === 0 && (
                <Paper sx={{ ...glassCardSx, p: 0, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                            <TableRow sx={{ bgcolor: '#0f172a' }}>
                                <TableCell sx={{ color: '#94a3b8', borderBottom: '1px solid #334155' }}>MATERIAL</TableCell>
                                
                                <TableCell align="right" sx={{ color: '#94a3b8', borderBottom: '1px solid #334155' }}>
                                    <Tooltip title="Precio Medio Ponderado por volumen en el histórico.">
                                        <span style={{ cursor: 'help', borderBottom: '1px dotted #64748b' }}>PRECIO MEDIO</span>
                                    </Tooltip>
                                </TableCell>
                                
                                <TableCell align="center" sx={{ color: '#94a3b8', borderBottom: '1px solid #334155' }}>
                                    <Tooltip title="Variación 90 días vs 90 días previos.">
                                        <span style={{ cursor: 'help', borderBottom: '1px dotted #64748b' }}>TENDENCIA (90d)</span>
                                    </Tooltip>
                                </TableCell>
                                
                                <TableCell align="right" sx={{ color: '#94a3b8', borderBottom: '1px solid #334155' }}>
                                    <Tooltip title="Volumen total de compra acumulado.">
                                        <span>GASTO TOTAL</span>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                            <TableBody>
                                {loadingSkus ? (
                                    <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3 }}><CircularProgress size={24} /></TableCell></TableRow>
                                ) : skus.map((row) => (
                                    <TableRow key={row.id} hover sx={{ '&:last-child td': { borderBottom: 0 }, '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }}>
                                        <TableCell sx={{ color: '#e2e8f0', fontFamily: 'monospace', fontWeight: 600 }}>{row.name}</TableCell>
                                        
                                        {/* PRECIO CON MONEDA INTEGRADA */}
                                        <TableCell align="right" sx={{ color: '#cbd5e1', fontFamily: 'monospace' }}>
                                            {row.avg_price.toLocaleString('es-ES', { style: 'currency', currency: row.currency || 'EUR', minimumFractionDigits: 2 })}
                                        </TableCell>
                                        
                                        <TableCell align="center">
                                            <TrendIndicator value={row.trend_pct} />
                                        </TableCell>
                                        
                                        {/* GASTO CON MONEDA INTEGRADA */}
                                        <TableCell align="right" sx={{ color: '#38bdf8', fontWeight: 700, fontFamily: 'monospace' }}>
                                            {row.total_spend.toLocaleString('es-ES', { style: 'currency', currency: row.currency || 'EUR', maximumFractionDigits: 0 })}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* TABLA 2: PROVEEDORES (CON DESPLEGABLE) */}
            {tabIndex === 1 && (
                <Paper sx={{ ...glassCardSx, p: 0, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                            <TableRow sx={{ bgcolor: '#0f172a' }}>
                                <TableCell sx={{ color: '#94a3b8', borderBottom: '1px solid #334155' }}>PROVEEDOR</TableCell>
                                
                                <TableCell align="center" sx={{ color: '#94a3b8', borderBottom: '1px solid #334155' }}>
                                    <Tooltip title="Nº de facturas/albaranes procesados en el histórico completo.">
                                        <span style={{ cursor: 'help', borderBottom: '1px dotted #64748b' }}>TRANSACCIONES</span>
                                    </Tooltip>
                                </TableCell>
                                
                                <TableCell align="center" sx={{ color: '#94a3b8', borderBottom: '1px solid #334155' }}>
                                    <Tooltip title="Variación del Precio Medio Ponderado: Últimos 90 días vs. los 90 días previos.">
                                        <span style={{ cursor: 'help', borderBottom: '1px dotted #64748b' }}>TENDENCIA (90d)</span>
                                    </Tooltip>
                                </TableCell>
                                
                                <TableCell align="right" sx={{ color: '#94a3b8', borderBottom: '1px solid #334155' }}>
                                    <Tooltip title="Volumen de compra acumulado en el histórico.">
                                        <span>GASTO TOTAL</span>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                            <TableBody>
                                {loadingProviders ? (
                                    <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3 }}><CircularProgress size={24} /></TableCell></TableRow>
                                ) : providers.map((row) => (
                                    <ProviderRow key={row.id} row={row} familyId={familyId} />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* MODAL DE EDICIÓN */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} PaperProps={{ sx: { bgcolor: '#1e293b', color: '#fff', border: '1px solid #334155' } }}>
                <DialogTitle>Renombrar Familia</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Nombre de la Familia"
                        fullWidth
                        variant="outlined"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        sx={{ 
                            mt: 1, 
                            '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: '#475569' } },
                            '& .MuiInputLabel-root': { color: '#94a3b8' }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditOpen(false)} sx={{ color: '#94a3b8' }}>Cancelar</Button>
                    <Button onClick={() => renameMutation.mutate()} variant="contained" sx={{ bgcolor: '#38bdf8', color: '#0f172a' }}>
                        Guardar
                    </Button>
                </DialogActions>
            </Dialog>

        </Container>
    );
};

export default ProcurementAnalysisPage;